import type {
  AdminQueryCountInput,
  AdminQueryGetInput,
  AdminQueryGetResult,
} from '@rb/shared/admin-query.types';
import { parseIsoToEpochMs } from '@rb/shared/time-utils';
import type { UserSpec } from '@rb/shared/user-state/user-spec';
import { AdminRPCClient } from '@/client/rpc-client';
import type { ServerSeedKey } from '@/client/seed.client';
import {
  buildRewardOptions,
  type CharacterSource,
  type PetSource,
  type RewardOption,
} from '@/components/admin/mail/reward-options';
import { fetchSheetCollection } from '@/components/admin/mail/sheet-source';
import { getL10nBase, getSheetEnv } from '@/lib/cdnBase';
import { loadTranslations, translateKey } from '@/master-data/lib/translations';

type SeededDataForComparison = Partial<Record<string, unknown[]>>;

/**
 * Admin RPC helpers used by admin-app panels.
 *
 * Mirrors the subset of `src/client/admin.client.ts` that the admin-app
 * pages currently consume. Routed through `AdminRPCClient` (active verse
 * connection) instead of the game-side `RPCClient`.
 */

export async function adminGetGlobalState(keys?: string[]): Promise<Record<string, unknown>> {
  return AdminRPCClient.call<Record<string, unknown>>('adminGetGlobalState', [keys]);
}

// --- Master Data Seeding (compare CDN JSON vs seeded DB) ---

export async function getSeededDataForComparison(
  seedKeys: ServerSeedKey[]
): Promise<SeededDataForComparison> {
  return AdminRPCClient.call<SeededDataForComparison>('getSeededDataForComparison', [seedKeys]);
}

// --- User detail / management ---

export async function getUserDetail(account: string): Promise<{
  state: Record<string, unknown> | null;
  assets: Record<string, number>;
}> {
  return AdminRPCClient.call('getUserDetail', [account]);
}

export async function adjustUserAsset(
  account: string,
  assetId: string,
  delta: number
): Promise<{ assetId: string; delta: number; balance: number; account: string }> {
  return AdminRPCClient.call('adjustUserAsset', [account, assetId, delta]);
}

/** 유저 성장/로드아웃 스펙 전체 교체 (dev/staging 전용 — prod 서버 차단). */
export async function overrideUserSpec(
  account: string,
  spec: UserSpec
): Promise<{ account: string; applied: string[] }> {
  return AdminRPCClient.call('adminOverrideUserSpec', [account, spec]);
}

/** Quick grant kind — d.unlockAll() 을 4개 독립 부여로 쪼갠 것 (dev/staging 전용). */
export type QuickGrantKind = 'upgrades' | 'skins' | 'pets' | 'breakthrough';

/** 대상 유저에게 "전체 부여"를 kind 단위로 적용 (dev/staging 전용 — prod 서버 차단). */
export async function adminQuickGrant(
  account: string,
  kind: QuickGrantKind
): Promise<{ account: string; kind: QuickGrantKind; count: number }> {
  return AdminRPCClient.call('adminQuickGrant', [account, kind]);
}

export async function searchUserByNickname(
  nickname: string
): Promise<{ account: string | null; nickname: string | null }> {
  return AdminRPCClient.call('searchUserByNickname', [nickname]);
}

// --- Stage / tutorial progress override (staging·dev 전용; prod 는 서버 차단) ---

/** 대상 유저의 lastClearedStageId 설정. stageId 0 = 리셋(null). */
export async function setUserStage(
  account: string,
  stageId: number
): Promise<{ account: string; lastClearedStageId: number | null }> {
  return AdminRPCClient.call('adminSetUserStage', [account, stageId]);
}

/** 대상 유저의 completedTutorials 셋 덮어쓰기. 빈 배열 = 리셋. */
export async function setUserTutorials(
  account: string,
  tutorialIds: string[]
): Promise<{ account: string; completedTutorials: string[] }> {
  return AdminRPCClient.call('adminSetUserTutorials', [account, tutorialIds]);
}

/** 대상 유저의 광고제거(adRemovalPurchased) 플래그 온오프. */
export async function setAdRemoval(
  account: string,
  enabled: boolean
): Promise<{ account: string; adRemovalPurchased: boolean }> {
  return AdminRPCClient.call('adminSetAdRemoval', [account, enabled]);
}

// --- Progression time control (staging·dev 전용; prod 는 서버 차단) ---

/** 출석을 N일 앞으로: 다음 출석 claim 1회를 연다. kind=event(이벤트 출석)/regular(일반 출석). */
export async function advanceAttendance(
  account: string,
  days: number,
  kind: 'event' | 'regular' = 'event'
): Promise<{ account: string; lastClaimDate: string }> {
  return AdminRPCClient.call('adminAdvanceAttendance', [account, days, kind]);
}

/** 출석 checkInCount 직접 세팅(6 → 다음 claim 7일차). RB-1729 재현용. kind=event/regular. */
export async function setAttendanceCheckInCount(
  account: string,
  count: number,
  kind: 'event' | 'regular' = 'event'
): Promise<{ account: string; checkInCount: number }> {
  return AdminRPCClient.call('adminSetAttendanceCheckInCount', [account, count, kind]);
}

/** DAILY 또는 WEEKLY 퀘스트 reset(다시 claim 가능). */
export async function resetQuests(
  account: string,
  scope: 'DAILY' | 'WEEKLY'
): Promise<{ account: string; resetCount: number }> {
  return AdminRPCClient.call('adminResetQuests', [account, scope]);
}

/**
 * 타임드 퀘스트(벽시계 기반) 상태를 강제 조작한다 — QA 가 n분을 기다리지 않고도
 * 만료/임박 카드 상태를 재현. clear=비움, expire=즉시 만료, imminent=30초 후 만료.
 */
export type TimedQuestResetMode = 'clear' | 'expire' | 'imminent';

export async function resetTimedQuests(
  account: string,
  mode: TimedQuestResetMode
): Promise<{ ok: true }> {
  return AdminRPCClient.call('adminResetTimedQuests', [account, mode]);
}

/** 모든 패스 activatedAt 을 N일 과거로: N레벨 추가 해금. */
export async function advancePass(
  account: string,
  days: number
): Promise<{ account: string; advancedCount: number }> {
  return AdminRPCClient.call('adminAdvancePass', [account, days]);
}

export interface GrantPassResult {
  account: string;
  productId: string;
  granted: string[];
  entitlements: string[];
  activatedAt: number;
  expiresAt: number;
  claimedLevel: number;
  unlockedLevel: number;
  totalLevels: number;
  clamped: boolean;
  /** elapsedDays 로 되감긴 패스 개수(지급 상품만이 아니라 대상 계정의 모든 패스). */
  shiftedPasses: number;
  /** elapsedDays 로 되감긴 entitlement 퍽 개수. */
  shiftedEntitlements: number;
}

/** 대상 유저에게 패스를 지급한다 (staging·dev 전용; prod 는 서버 차단). */
export async function grantPass(
  account: string,
  productId: string,
  metadata: string,
  opts: { elapsedDays: number; claimedLevel?: number; mode: 'reset' | 'stack' }
): Promise<GrantPassResult> {
  return AdminRPCClient.call('adminGrantPass', [account, productId, metadata, opts]);
}

// --- Star adjustment (staging·dev 전용; prod 는 서버 차단) ---

/** 누적 별 수(totalStarCount) 절대 설정. 별모으기 이벤트/TOTAL_STAR_COUNT 퀘스트 재현용. */
export async function setTotalStarCount(
  account: string,
  count: number
): Promise<{ account: string; totalStarCount: number }> {
  return AdminRPCClient.call('adminSetTotalStarCount', [account, count]);
}

/** 특정 스테이지 별점(0~3) 절대 설정. stageBestScores 갱신 + 누적 별 delta 보정. */
export async function setStageStars(
  account: string,
  stageId: number,
  stars: number
): Promise<{ account: string; stageId: number; stars: number; totalStarCount: number }> {
  return AdminRPCClient.call('adminSetStageStars', [account, stageId, stars]);
}

// --- Valhalla progress override (staging·dev 전용; prod 는 서버 차단) ---
// 조회(getValhallaSeasonTiers)만 prod 에서도 허용 — 쓰기 2종은 서버가 거부한다.
// 핸들러는 `adminValhalla` 네임스페이스(admin-valhalla-progress.handler.ts).

export interface ValhallaSeasonTiers {
  /**
   * 활성 시즌이 없으면 null — 발할라 모드가 닫힌 상태.
   * 시즌·티어 코덱 스키마에 `name` 이 없어 서버가 라벨을 못 만든다 — id 와 기간·웨이브 수로 식별.
   */
  season: { id: number; startTime: number; endTime: number } | null;
  tiers: { id: number; waveCount: number }[];
}

export interface ValhallaProgressResult {
  account: string;
  valhallaSeasonId: number;
  lastClearedValhallaTierId: number;
  valhallaHighestWave: Record<number, number>;
}

/** 활성 발할라 시즌 + 그 시즌의 티어 목록(id 오름차순, 총 웨이브 수 포함). */
export async function getValhallaSeasonTiers(): Promise<ValhallaSeasonTiers> {
  return AdminRPCClient.call('adminValhalla.getActiveSeasonTiers', []);
}

/** 활성 시즌에서 tierId 까지 클리어 상태로 설정(이하 티어 풀웨이브). 0 = 리셋. */
export async function setValhallaClearedTier(
  account: string,
  tierId: number
): Promise<ValhallaProgressResult> {
  return AdminRPCClient.call('adminValhalla.setClearedTier', [account, tierId]);
}

/** 활성 시즌 특정 티어의 최고 도달 웨이브 설정(0 = 기록 삭제). 해금은 바뀌지 않는다. */
export async function setValhallaTierWave(
  account: string,
  tierId: number,
  wave: number
): Promise<ValhallaProgressResult> {
  return AdminRPCClient.call('adminValhalla.setTierWave', [account, tierId, wave]);
}

// --- User browser + bulk delete ---

export async function adminQueryCount(input: AdminQueryCountInput): Promise<number> {
  return AdminRPCClient.call<number>('admin.queryCount', [input]);
}

export async function adminQueryGet(input: AdminQueryGetInput): Promise<AdminQueryGetResult> {
  return AdminRPCClient.call<AdminQueryGetResult>('admin.queryGet', [input]);
}

export async function deleteUserData(account: string): Promise<{ success: boolean }> {
  return AdminRPCClient.call('deleteUserData', [account]);
}

// Ban handlers live in `@Handler('adminBan')` (admin-ban.handler.ts) — NOT `admin`.
// The V8 SDK registers one class per @Handler namespace; `admin` is already owned by
// AdminWorkerHandler, so AdminBanHandler must use a distinct namespace or its methods
// never register (resulting in "Function not found"). See admin-ban.handler.ts.
export async function banUser(account: string, reason?: string): Promise<{ success: boolean }> {
  return AdminRPCClient.call('adminBan.banUser', [{ account, reason }]);
}

export async function unbanUser(account: string): Promise<{ success: boolean }> {
  return AdminRPCClient.call('adminBan.unbanUser', [account]);
}

export async function isBanned(
  account: string
): Promise<{ banned: boolean; reason?: string; bannedAt?: number; bannedBy?: string }> {
  return AdminRPCClient.call('adminBan.isBanned', [account]);
}

// --- Record (history / suspicious) management ---
// Server: AdminWorkerHandler (@Handler('admin')) — see admin-worker.handler.ts

export type AdminRecordType =
  | 'SEED'
  | 'STAGE_HISTORY'
  | 'LEAGUE_HISTORY'
  | 'VALHALLA_HISTORY'
  | 'DEGEN_HISTORY'
  | 'SUSPICIOUS_LOG';

export async function queryRecordsPage(options: {
  type: AdminRecordType;
  account: string;
  validated?: boolean | null;
  cursor?: string | null;
  limit?: number;
}): Promise<{
  records: Record<string, unknown>[];
  total: number;
  hasMore: boolean;
  nextCursor: string | null;
}> {
  return AdminRPCClient.call('admin.queryRecordsPage', [options]);
}

export async function adminClearUserRecords(options: {
  type: AdminRecordType;
  account: string;
}): Promise<{ cleared: number }> {
  return AdminRPCClient.call('admin.adminClearUserRecords', [options]);
}

export async function adminDeleteUserRecord(options: {
  type: AdminRecordType;
  account: string;
  matcher: { sessionId?: string; timestamp?: number };
}): Promise<{ removed: number }> {
  return AdminRPCClient.call('admin.adminDeleteUserRecord', [options]);
}

// --- Currency transactions (per-user mint/burn audit log) ---
// Server: AdminWorkerHandler (@Handler('admin')) — see admin-worker.handler.ts

export interface CurrencyTxRow {
  /** V8 collection doc id — 행 key 로 사용. */
  __id?: string;
  account: string;
  type: 'mint' | 'burn';
  assetId: string;
  amount: number;
  reason: string;
  timestamp: number;
  memo?: string;
}

/** 대상 유저의 통화 트랜잭션(mint/burn) 페이지 조회. 읽기 전용 감사 로그. */
export async function queryCurrencyTransactionPage(options: {
  account: string;
  assetId?: string;
  type?: 'mint' | 'burn';
  cursor?: string | null;
  limit?: number;
}): Promise<{
  records: CurrencyTxRow[];
  total: number;
  hasMore: boolean;
  nextCursor: string | null;
}> {
  return AdminRPCClient.call('admin.queryCurrencyTransactionPage', [options]);
}

/**
 * staging/dev 전용 — ALL 유저의 `currencyTransactions` + `currencyTransactionsV2`
 * 컬렉션을 통째로 삭제한다(계정 필터 없음). prod verse 에서는 서버가 throw 한다.
 */
export async function clearAllCurrencyTransactions(): Promise<{ cleared: string[] }> {
  return AdminRPCClient.call('adminClearCurrencyTransactions', []);
}

// --- Season rankings (admin raw) ---
// Server: AdminRankHandler (@Handler('adminRank')) — see admin-rank.handler.ts

export type RankingKind = 'LEAGUE' | 'DEGEN';

export interface SeasonMeta {
  id: number;
  name: string;
  startTime: number;
  endTime: number;
}

/**
 * R2 `clientLeagueSeasons` row — object form; `name` is an l10n key. Times are
 * epoch ms on the live CDN but older/local fixtures still ship ISO+offset
 * strings, so the field is `string | number` and decoded defensively.
 */
interface LeagueSeasonJson {
  id: number;
  name?: string;
  startTime?: string | number;
  endTime?: string | number;
}

/** ISO+offset 문자열이든 epoch ms 숫자든 epoch ms 로 정규화한다. 절대 throw 하지 않는다. */
function toEpochMs(v: string | number | undefined): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string' && v) {
    try {
      return parseIsoToEpochMs(v);
    } catch {
      return 0;
    }
  }
  return 0;
}

/**
 * 시즌 셀렉터용 메타를 R2 결과 JSON 에서 디코드한다.
 *
 * verse 정적 RPC(`adminRank.listSeasons`)는 codec 으로 인코딩된 positional 튜플을
 * 반환하는데, 시즌 codec 스키마(`league-season`/`degen-season`)에는 `id` 도 표시
 * `name` 도 살아있지 않아 셀렉터에서 `#undefined undefined` 로 떴다. 대신 클라
 * 게임과 동일한 R2 산출물을 직접 읽는다:
 *  - LEAGUE: `clientLeagueSeasons` (객체; name=l10n 키, 시간=epoch ms 또는 ISO 문자열)
 *  - DEGEN : `serverDegenSeasons`  (compact codec 튜플 `[id, startMs, endMs]`, name 없음)
 */
export function decodeSeasonsFromR2(
  kind: RankingKind,
  raw: unknown,
  resolveName: (key?: string) => string = (k) => k ?? ''
): SeasonMeta[] {
  if (!Array.isArray(raw)) return [];

  if (kind === 'DEGEN') {
    return raw
      .filter((r): r is [number, number, number] => Array.isArray(r) && typeof r[0] === 'number')
      .map(([id, startTime, endTime]) => ({
        id,
        name: `시즌 ${id}`,
        startTime: toEpochMs(startTime),
        endTime: toEpochMs(endTime),
      }))
      .sort((a, b) => b.id - a.id);
  }

  return raw
    .filter((r): r is LeagueSeasonJson => !!r && typeof (r as LeagueSeasonJson).id === 'number')
    .map((s) => {
      // l10n 키에 시트發 개행/공백이 섞여 들어오므로 조회 전에 제거한다.
      const key = s.name?.replace(/\s+/g, '');
      const resolved = resolveName(key).trim();
      return {
        id: s.id,
        name: resolved || `시즌 ${s.id}`,
        startTime: toEpochMs(s.startTime),
        endTime: toEpochMs(s.endTime),
      };
    })
    .sort((a, b) => b.id - a.id);
}

/**
 * 시즌 메타 목록(id desc). 클라 게임과 동일한 R2 결과 JSON 에서 읽는다 —
 * verse RPC 의 codec 튜플은 id/name 이 없어 셀렉터가 깨졌다. league 의 l10n
 * 키 이름은 admin l10n('ko')으로 best-effort 해석한다(실패 시 키 fallback).
 */
export async function adminListSeasons(kind: RankingKind): Promise<SeasonMeta[]> {
  const cdnKey = kind === 'DEGEN' ? 'serverDegenSeasons' : 'clientLeagueSeasons';
  const raw = await fetchSheetCollection<unknown[]>(cdnKey);

  if (kind === 'DEGEN') return decodeSeasonsFromR2(kind, raw);

  let resolveName = (key?: string): string => key ?? '';
  try {
    await loadTranslations('ko', getL10nBase(getSheetEnv()));
    resolveName = (key?: string): string => (key ? translateKey(key, 'ko') : '');
  } catch {
    // l10n 은 best-effort — 실패하면 라벨에 raw l10n 키가 노출된다(여전히 식별 가능).
  }
  return decodeSeasonsFromR2(kind, raw, resolveName);
}

export async function adminGetSeasonRankings(options: {
  kind: RankingKind;
  seasonId: number;
  limit?: number;
  startAfter?: string;
}): Promise<{ rows: Record<string, unknown>[]; hasMore: boolean; nextCursor: string | null }> {
  return AdminRPCClient.call('adminRank.getSeasonRankings', [options]);
}

// 서버 getCollectionItems 가 호출당 100행 상한이라 페이지 크기는 100 으로 요청해야
// hasMore 가 정확하다(MAX_LIMIT 도 100 으로 클램프됨). 1000페이지 × 100 = 100k row hard cap.
const RANKING_PAGE = 100;
const MAX_RANKING_PAGES = 1000;

/** Cursor 루프로 시즌 랭킹 전 페이지를 모아 rank 를 누적 부여. */
export async function fetchAllSeasonRankings(
  kind: RankingKind,
  seasonId: number
): Promise<Record<string, unknown>[]> {
  const all: Record<string, unknown>[] = [];
  let cursor: string | undefined;
  let rank = 0;
  for (let page = 0; page < MAX_RANKING_PAGES; page++) {
    const res = await adminGetSeasonRankings({
      kind,
      seasonId,
      limit: RANKING_PAGE,
      startAfter: cursor,
    });
    for (const row of res.rows) all.push({ ...row, rank: ++rank });
    if (!res.hasMore || !res.nextCursor) break;
    cursor = res.nextCursor;
  }
  return all;
}

// --- Season validation status ---
// Server: AdminWorkerHandler (@Handler('admin')) — see admin-worker.handler.ts

export interface ValidationRankingRow {
  account: string;
  score: number;
  validated: boolean;
  flagged: boolean;
  banned: boolean;
}

export interface SeasonValidationSummary {
  totalRanked: number;
  validated: number;
  pending: number;
}

export interface SeasonValidationStatus {
  rankings: ValidationRankingRow[];
  summary: SeasonValidationSummary;
}

export async function getSeasonValidationStatus(seasonId: number): Promise<SeasonValidationStatus> {
  return AdminRPCClient.call('admin.getSeasonValidationStatus', [{ seasonId }]);
}

// --- Admin mail send ---

/**
 * 보상 피커 옵션 — R2 결과 JSON(`clientPets`/`clientCharacters`)을 사용.
 *
 * verse 정적 RPC(`pet.getPets`/`character.getCharacters`)는 codec 으로 인코딩된
 * positional 튜플을 반환하고 표시 이름이 없어 라벨이 전부 undefined 가 된다.
 * 대신 클라 게임과 동일한 R2 readable 컬렉션을 받아 id/스킨을 얻고, l10n 키
 * 형태의 이름은 admin l10n('ko')으로 해석한다(실패 시 키 그대로 fallback).
 */
export async function getMailRewardOptions(): Promise<RewardOption[]> {
  const [pets, characters] = await Promise.all([
    fetchSheetCollection<PetSource[]>('clientPets'),
    fetchSheetCollection<CharacterSource[]>('clientCharacters'),
  ]);

  let resolveName = (key?: string): string => key ?? '';
  try {
    await loadTranslations('ko', getL10nBase(getSheetEnv()));
    resolveName = (key?: string): string => (key ? translateKey(key, 'ko') : '');
  } catch {
    // l10n 은 best-effort — 실패하면 라벨에 raw l10n 키가 노출된다(여전히 식별 가능).
  }

  return buildRewardOptions(pets ?? [], characters ?? [], resolveName);
}
